import React, { useEffect, useRef, useState } from 'react';
import styles from './styles/styles.module.css';
import TopBar from './TopBar';
import { useDispatch, useSelector } from 'react-redux';
import TimePick from '../TimePick/TimePick';
import moment from 'moment';
import 'moment-timezone';
import { FaCircleArrowLeft, FaCircleArrowRight } from 'react-icons/fa6';
import DatePicker from 'react-datepicker';
import { MdDataSaverOn } from 'react-icons/md';
import {
  createLogEntry,
  findLogEntriesByDate,
  storeShiftToDo,
} from '../../api/logEntries/post';
import { setLogEntires } from '../../Redux/slices/logEntires';
import 'react-datepicker/dist/react-datepicker.css';
import { BeatLoader, MoonLoader } from 'react-spinners';
import FilterAndExport from './FilterAndExport';
import { setShiftToDoItems } from '../../Redux/slices/shiftToDoSlice';

//......
/** ---------- Business day helper ----------
 * current "business day" in tz given shiftStart "HH:mm"
 * If now >= today's shift start → business day = today
 * Else → business day = yesterday
 */
function getCurrentBusinessDay(tz: string, shiftStart: string) {
  const now = moment().tz(tz);
  const [sh, sm] = shiftStart.split(':').map(Number);
  const shiftStartToday = now
    .clone()
    .startOf('day')
    .add(sh, 'hours')
    .add(sm, 'minutes');

  const bizStart = now.isSameOrAfter(shiftStartToday)
    ? now.clone().startOf('day')
    : now.clone().subtract(1, 'day').startOf('day');

  return bizStart;
}

/** ---------- LineItem with Save + Priority dots ---------- */
const LineItem: React.FC<{
  entryId?: string;
  time: string;
  description: string;
  invalidated?: boolean;
  onSetPriority: (level: 'high' | 'medium' | 'low') => Promise<void> | void;
  disabled?: boolean;
}> = ({ entryId, time, description, invalidated, onSetPriority, disabled }) => {
  const [showPriority, setShowPriority] = useState(false);
  const [settingPriority, setSettingPriority] = useState(false);

  const handleSaveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    setShowPriority((prev) => !prev);
  };

  const handlePriorityClick = async (level: 'high' | 'medium' | 'low') => {
    setSettingPriority(true);

    await onSetPriority(level);
    setShowPriority(false);
    setSettingPriority(false);
  };

  return (
    <div
      className={`${styles.lineItem} ${invalidated ? styles.lineItemInvalid : ''}`}
      // Close the dots if the row itself is clicked
      onClick={() => showPriority && setShowPriority(false)}
    >
      <p className={styles.lineItemTime}>{time}</p>
      <p className={styles.lineItemDescription} title={description}>
        {description}
      </p>

      {/* Hover action icon (shown on :hover via CSS) */}
      <button
        type="button"
        className={styles.lineItemSave}
        aria-label="Add to To-Do"
        onClick={handleSaveClick}
        disabled={disabled}
      >
        <MdDataSaverOn />
      </button>

      {/* Priority buttons, appear when save icon is clicked */}
      {showPriority &&
        (settingPriority ? (
          <BeatLoader />
        ) : (
          <div
            className={styles.priorityButtons}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={`${styles.priorityBtn} ${styles.high}`}
              title="High priority"
              onClick={() => handlePriorityClick('high')}
            />
            {/* <button
            className={`${styles.priorityBtn} ${styles.medium}`}
            title="Medium priority"
            onClick={() => handlePriorityClick('medium')}
          /> */}
            <button
              className={`${styles.priorityBtn} ${styles.low}`}
              title="Low priority"
              onClick={() => handlePriorityClick('low')}
            />
          </div>
        ))}
    </div>
  );
};

const DailyLog: React.FC = () => {
  const dispatch = useDispatch();
  const { logEntries } = useSelector((state: any) => state.logEntries);
  const { expanded } = useSelector((state: any) => state.bottomBar);
  const { timezone, shiftStart } = useSelector(
    (state: any) => state.currentSite
  );

  // Initial "business day" anchor for the UI (key + display)
  const initialBizDay = getCurrentBusinessDay(timezone, shiftStart);
  const [fetchDate, setFetchDate] = useState({
    key: initialBizDay.format('MMDDYYYY'),
    displayDate: initialBizDay.format('MMMM DD, YYYY (dddd)'),
  });

  const [manualLogEntry, setManualLogEntry] = useState<string>('');
  const [time, setTime] = useState<string | null>(() =>
    moment().tz(timezone).format('HH:mm')
  );

  /**
   * Once the operator manually changes the time picker, stop the automatic
   * clock sync until the entry is submitted or we explicitly reset it.
   *
   * Without this guard the 5-second interval below can overwrite a manually
   * selected earlier time almost immediately, which makes the picker feel
   * like it is "snapping back" to the current time.
   */
  const [manualTimeSelected, setManualTimeSelected] = useState(false);

  const [isToday, setIsToday] = useState(true); // "today" = current business day
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [navigationLoading, setNavigationLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Track per-item priority saving state (entryId → boolean)
  const [savingPriorityId, setSavingPriorityId] = useState<string | null>(null);

  const datePickerRef = useRef<HTMLDivElement>(null);
  const ignoreCloseRef = useRef(false);

  // Close the datepicker if click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ignoreCloseRef.current) {
        ignoreCloseRef.current = false;
        return;
      }
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setShowDatePicker(false);
      }
    }
    if (showDatePicker) {
      window.addEventListener('mousedown', handleClickOutside);
    }
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, [showDatePicker]);

  // Keep the picker synchronized with the current time only until the
  // operator manually changes it.
  useEffect(() => {
    const interval = setInterval(() => {
      if (isToday && !manualTimeSelected) {
        setTime(moment().tz(timezone).format('HH:mm'));
      }
    }, 5 * 1000);

    return () => clearInterval(interval);
  }, [timezone, isToday, manualTimeSelected]);

  /**
   * TimePick receives this wrapper instead of React's raw setTime.
   * Any user-originated picker change freezes the automatic clock updates.
   */
  const handleManualTimeChange: React.Dispatch<
    React.SetStateAction<string | null>
  > = (nextTime) => {
    setManualTimeSelected(true);
    setTime(nextTime);
  };

  async function handleDateBack() {
    if (navigationLoading) return;
    setNavigationLoading(true);
    try {
      const newDate = moment(fetchDate.key, 'MMDDYYYY').subtract(1, 'day');
      const res = await findLogEntriesByDate(newDate.format('MMDDYYYY'));
      if (res?.success) {
        dispatch(setLogEntires(res.data));
        const bizToday = getCurrentBusinessDay(timezone, shiftStart);
        setFetchDate({
          key: newDate.format('MMDDYYYY'),
          displayDate: newDate.format('MMMM DD, YYYY (dddd)'),
        });
        const viewingToday = newDate.isSame(bizToday, 'day');

        setIsToday(viewingToday);
        setManualTimeSelected(false);

        if (viewingToday) {
          setTime(moment().tz(timezone).format('HH:mm'));
        }
      }
    } catch {
      alert('Something went wrong. Please refresh the page and try again');
    } finally {
      setNavigationLoading(false);
    }
  }

  async function handleGoForward() {
    if (navigationLoading || isToday) return;
    setNavigationLoading(true);
    try {
      const newDate = moment(fetchDate.key, 'MMDDYYYY').add(1, 'day');
      const res = await findLogEntriesByDate(newDate.format('MMDDYYYY'));
      if (res?.success) {
        dispatch(setLogEntires(res.data));
        const bizToday = getCurrentBusinessDay(timezone, shiftStart);
        setFetchDate({
          key: newDate.format('MMDDYYYY'),
          displayDate: newDate.format('MMMM DD, YYYY (dddd)'),
        });
        const viewingToday = newDate.isSame(bizToday, 'day');

        setIsToday(viewingToday);
        setManualTimeSelected(false);

        if (viewingToday) {
          setTime(moment().tz(timezone).format('HH:mm'));
        }
      }
    } catch {
      alert('Something went wrong. Please refresh the page and try again');
    } finally {
      setNavigationLoading(false);
    }
  }

  async function handleSubmit() {
    setSubmitLoading(true);
    try {
      if (manualLogEntry.length < 2 && !time) {
        alert('Add a time and description for manual log entries');
        return;
      }

      if (time) {
        const now = moment().tz(timezone);

        // ----- Build "actual" timestamp from BUSINESS DAY + picked time -----
        // If picked time is before shiftStart → actual calendar day is bizDay + 1
        // Else → actual calendar day is bizDay
        const [hour, minute] = time.split(':').map(Number);

        const bizDay = moment(fetchDate.key, 'MMDDYYYY')
          .tz(timezone)
          .startOf('day');
        const [sh, sm] = shiftStart.split(':').map(Number);
        const pickedMinutes = hour * 60 + minute;
        const shiftMinutes = sh * 60 + sm;

        const actualCalendarDay =
          pickedMinutes < shiftMinutes
            ? bizDay.clone().add(1, 'day')
            : bizDay.clone();

        const selectedTimestamp = actualCalendarDay
          .clone()
          .startOf('day')
          .add(hour, 'hours')
          .add(minute, 'minutes');

        // ----- Validation rules -----
        const todayCal = now.clone().startOf('day');
        const todayShiftStart = todayCal
          .clone()
          .add(sh, 'hours')
          .add(sm, 'minutes');

        // 1) Prevent future time only if logging for today's calendar day
        const isForTodayCalendar = actualCalendarDay.isSame(todayCal, 'day');
        if (isForTodayCalendar && selectedTimestamp.isAfter(now)) {
          alert('Selected time cannot be in the future.');
          return;
        }

        // 2) Enforce "no before shift" only if logging for today's calendar day
        //    AND today's shift has started. (Before shift → allow.)
        const todaysShiftHasStarted = now.isSameOrAfter(todayShiftStart);
        if (
          isForTodayCalendar &&
          todaysShiftHasStarted &&
          selectedTimestamp.isBefore(todayShiftStart)
        ) {
          alert("You can't log entries before the shift start for today.");
          return;
        }

        // ----- Submit -----
        const resp = await createLogEntry(
          'ACTIVITY_ENTRY',
          manualLogEntry,
          time
        );
        if (resp?.success) {
          // After submit, reload *current business day*
          const biz = getCurrentBusinessDay(timezone, shiftStart);
          const todayKey = biz.format('MMDDYYYY');
          const todayDisplay = biz.format('MMMM DD, YYYY (dddd)');

          const reload = await findLogEntriesByDate(todayKey);
          if (reload?.success) {
            dispatch(setLogEntires(reload.data));
            setFetchDate({ key: todayKey, displayDate: todayDisplay });
            setIsToday(true);
          }

          setManualLogEntry('');
          setTime(moment().tz(timezone).format('HH:mm'));
          setManualTimeSelected(false);
        }
      }
    } catch {
      alert('Something went wrong, please refresh the page and try again');
    } finally {
      setSubmitLoading(false);
    }
  }

  async function handleDatePickerChange(date: Date | null) {
    if (!date) return;
    setTimeout(() => setShowDatePicker(false), 0);
    setShowDatePicker(false);
    setNavigationLoading(true);

    try {
      // User picks a calendar date; treat it as a business-day anchor for the UI
      const newDate = moment(date).tz(timezone);
      const res = await findLogEntriesByDate(newDate.format('MMDDYYYY'));
      if (res?.success) {
        dispatch(setLogEntires(res.data));
        const bizToday = getCurrentBusinessDay(timezone, shiftStart);
        setFetchDate({
          key: newDate.format('MMDDYYYY'),
          displayDate: newDate.format('MMMM DD, YYYY (dddd)'),
        });
        const viewingToday = newDate.isSame(bizToday, 'day');

        setIsToday(viewingToday);
        setManualTimeSelected(false);

        if (viewingToday) {
          setTime(moment().tz(timezone).format('HH:mm'));
        }
      }
    } catch {
      alert('Something went wrong. Please refresh the page and try again.');
    } finally {
      setNavigationLoading(false);
    }
  }

  async function setToDoItem(level: any, entryId: any) {
    try {
      setSavingPriorityId(entryId);
      const res = await storeShiftToDo({ entryId, level });

      if (res?.success) {
        const data = (res as any).data;

        const items = Array.isArray(data) ? data : [];
        dispatch(setShiftToDoItems(items));
      }
    } catch (err) {
      console.log(err);
    } finally {
      setSavingPriorityId(null);
    }
  }

  return (
    <div className={!expanded ? styles.wrapper : styles.wrapperExpanded}>
      <TopBar />
      <div
        className={
          !expanded ? styles.lineItemWrapper : styles.lineItemWrapperFull
        }
      >
        <div
          className={
            !expanded ? styles.logEntryContainer : styles.logEntryContainerFull
          }
        >
          {logEntries.map((entry: any) => {
            const entryId = entry?._id || String(entry?.dateTime || '');

            return (
              <LineItem
                key={entryId}
                entryId={entryId}
                invalidated={entry?.invalidated}
                time={moment(entry?.dateTime).tz(timezone).format('HH:mm')}
                description={entry?.description}
                disabled={savingPriorityId === entryId}
                onSetPriority={(level) => setToDoItem(level, entryId)}
              />
            );
          })}
        </div>

        {expanded ? (
          <div className={styles.expandedRight}>
            <div className={styles.arrowContainer}>
              <FaCircleArrowLeft
                onClick={!navigationLoading ? handleDateBack : undefined}
                className={`${styles.arrowLeft} ${navigationLoading ? styles.pulsing : ''} ${navigationLoading ? styles.disabledArrow : ''}`}
              />
              <div className={styles.datePickerWrapper}>
                <p
                  className={styles.displayedDate}
                  onClick={() => setShowDatePicker(!showDatePicker)}
                >
                  {fetchDate.displayDate}
                </p>

                {showDatePicker && (
                  <div
                    ref={datePickerRef}
                    className={styles.floatingDatePicker}
                    onMouseDown={() => {
                      // prevent the outside click handler from closing immediately
                      ignoreCloseRef.current = true;
                      setTimeout(() => {
                        ignoreCloseRef.current = false;
                      }, 0);
                    }}
                  >
                    <DatePicker
                      selected={moment(fetchDate.key, 'MMDDYYYY')
                        .tz(timezone)
                        .toDate()}
                      onChange={handleDatePickerChange}
                      inline
                      maxDate={new Date()}
                    />
                  </div>
                )}
              </div>
              <FaCircleArrowRight
                onClick={
                  !navigationLoading && !isToday ? handleGoForward : undefined
                }
                className={`${styles.arrowRight} ${navigationLoading || isToday ? styles.disabledArrow : ''} ${navigationLoading ? styles.pulsing : ''}`}
              />
            </div>

            <FilterAndExport />
          </div>
        ) : null}
      </div>

      <div className={styles.entryContainer}>
        <div className={styles.timePickContainer}>
          <TimePick time={time} setTime={handleManualTimeChange} />
        </div>

        <input
          value={manualLogEntry}
          onChange={(e) => setManualLogEntry(e.target.value)}
          className={styles.descriptionInput}
          placeholder="Write your log entry…"
        />
        <button
          disabled={submitLoading}
          onClick={handleSubmit}
          className={styles.submitBtn}
        >
          {submitLoading ? (
            <div className={styles.fadeLoaderContainer}>
              <MoonLoader color="#ffffff" size={16} loading={submitLoading} />
            </div>
          ) : (
            'Submit'
          )}
        </button>
      </div>
    </div>
  );
};

export default DailyLog;
